import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Linkedin, Twitter, Mail, ArrowRight } from 'lucide-react';
import Newsletter from '../components/Newsletter';

const teamMembers = [
  {
    name: 'Henry Dike',
    role: 'CEO & Founder',
    bio: 'Expert with years of experience in AI and Digital Health, Clinical Tech Research, and Project Management.',
    image: 'https://customer-assets.emergentagent.com/job_cureclave-tech/artifacts/ty3qd0aa_IMG_1956.jpeg',
    linkedin: 'https://linkedin.com',
    twitter: 'https://twitter.com',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function Team() {
  return (
    <div className="min-h-screen pt-20" data-testid="team-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-sm font-medium tracking-wider uppercase text-cyan-600 mb-4 block">
              Our Team
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6">
              Meet the Expert Behind CureClave
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              Leadership driving innovation in healthcare technology with expertise in AI, digital health, and clinical research.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20" data-testid="team-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="flex justify-center"
          >
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="team-card group bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 max-w-md"
                data-testid={`team-member-${index}`}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-80 object-cover object-center transition-transform duration-300"
                    style={{ objectPosition: '50% 35%' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-4 left-4 right-4 flex justify-center gap-3">
                      <a
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                        data-testid={`team-linkedin-${index}`}
                      >
                        <Linkedin className="w-5 h-5" />
                      </a>
                      <a
                        href={member.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                        data-testid={`team-twitter-${index}`}
                      >
                        <Twitter className="w-5 h-5" />
                      </a>
                      <a
                        href="mailto:hello@cureclave.com"
                        className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                        data-testid={`team-email-${index}`}
                      >
                        <Mail className="w-5 h-5" />
                      </a>
                    </div>
                  </div>
                </div>
                <div className="p-8 text-center">
                  <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
                    {member.name}
                  </h3>
                  <p className="text-cyan-600 font-medium mb-4">{member.role}</p>
                  <p className="text-slate-600">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Join Team CTA */}
      <section className="py-20 bg-slate-50" data-testid="join-team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-3xl p-12 md:p-16 text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Work With Us
            </h2>
            <p className="text-cyan-100 text-lg max-w-2xl mx-auto mb-8">
              Interested in partnering with CureClave Innovations? Let's discuss how we can transform healthcare technology together.
            </p>
            <Link to="/contact">
              <Button 
                className="bg-white text-cyan-600 hover:bg-slate-50 rounded-full px-8 py-4 text-base font-semibold shadow-lg"
                data-testid="join-team-cta"
              >
                Get in Touch
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
}
